/* eslint-disable no-param-reassign */
import { navigateTo, onNuxtReady } from '#imports'
import { type Ref, type ShallowReactive, nextTick, watch } from 'vue'
import {
  type LocationQueryRaw,
  type RouteLocationNormalizedLoaded,
  useRoute,
} from 'vue-router'

import { parse } from '@backmarket/utils/query-string/parse'

import { buildHash } from '../utils/buildHash'
import { fullyDecodeUriComponent } from '../utils/fullyDecodeUriComponent'

export function useFiltersFromUrl(
  filtersInput: ShallowReactive<Record<string, string[]>>,
  priceFilter: Ref<[number, number] | undefined>,
  pageInput: Ref<number>,
  {
    route = useRoute(),
    openFacets = [],
  }: {
    route?: Partial<RouteLocationNormalizedLoaded>
    openFacets?: string[]
  } = {},
) {
  function getFiltersFromUrl() {
    const { price, ...parsedQuery } = route.hash
      ? parse(route.hash)
      : { price: [] }

    const p = route.query
      ? parseInt(route.query.p?.toString() || '', 10) || 1
      : 1

    if (Array.isArray(price) && price.length === 2 && price[0] && price[1]) {
      priceFilter.value = [parseInt(price[0], 10), parseInt(price[1], 10)]
    }
    for (const [key, val] of Object.entries(parsedQuery)) {
      if (Array.isArray(val)) {
        filtersInput[key] = val
          .filter((item): item is string => item !== null)
          .map(fullyDecodeUriComponent)
      } else if (val !== null) {
        filtersInput[key] = [fullyDecodeUriComponent(val)]
      }
    }

    if (p > 1) {
      pageInput.value = p
    }
  }

  function cleanQueryParams(queryParams: LocationQueryRaw) {
    if (queryParams.p === 1) {
      const { p: _p, ...rest } = queryParams

      return rest
    }

    return queryParams
  }

  async function pushHashFiltersToRouter() {
    pageInput.value = 1

    const activeKey = openFacets.find((key) => route.query?.[key] != null)
    const nextQuery = cleanQueryParams({ ...route.query, p: pageInput.value })
    let filtersForHash: Record<string, string[]> = filtersInput

    if (activeKey) {
      const rawChipValue = route.query?.[activeKey]
      const chipValue = Array.isArray(rawChipValue)
        ? rawChipValue[0]
        : rawChipValue
      const currentOpenValues = filtersInput[activeKey] ?? []
      const openFilterUnchanged =
        chipValue != null &&
        currentOpenValues.length === 1 &&
        currentOpenValues[0] === fullyDecodeUriComponent(chipValue)

      if (openFilterUnchanged) {
        const { [activeKey]: _openFilter, ...otherFilters } = filtersInput
        filtersForHash = otherFilters
      } else {
        delete nextQuery[activeKey]
      }
    }

    const filters = priceFilter.value
      ? { ...filtersForHash, price: priceFilter.value }
      : filtersForHash
    const hashForActiveFilters = buildHash(filters)

    if (route.name) {
      await navigateTo({
        name: route.name,
        query: nextQuery,
        params: route.params,
        hash: hashForActiveFilters ? `#${hashForActiveFilters}` : '',
      })
    }
  }

  async function pushQueryFiltersToRouter() {
    if (route.name) {
      await navigateTo({
        name: route.name,
        query: cleanQueryParams({ ...route.query, p: pageInput.value }),
        params: route.params,
        hash: route.hash,
      })
      if (import.meta.client) window.scrollTo(0, 0)
    }
  }

  let initialSyncCompleted = false
  onNuxtReady(async () => {
    getFiltersFromUrl()
    // Ensures the watcher fires and is blocked before we open the gate,
    // preventing a page reset when loading with ?p=2 and hash filters.
    await nextTick()
    initialSyncCompleted = true
  })

  watch(
    [filtersInput, priceFilter],
    async () => {
      if (!initialSyncCompleted) return
      await pushHashFiltersToRouter()
    },
    { deep: true },
  )
  watch(pageInput, async () => {
    await pushQueryFiltersToRouter()
  })
}
